/**
 * Sprogsider — engelsk indhold (/en).
 *
 * REGLER: ingen priser eller varigheder skrives her. De hentes fra courseGroups.js,
 * så dansk og fremmedsprog aldrig kan vise to forskellige tal. Hver faktuel påstand
 * skal kunne føres tilbage til `sources`.
 */

const FS = (label, path) => ({ label, url: `https://www.fstyr.dk${path}` });

const AUTHORITY = "Færdselsstyrelsen (the Danish Road Traffic Authority)";

export const en = {
  code: "en",
  htmlLang: "en",
  dir: "ltr",
  locale: "en_GB",
  numLocale: "en-GB",
  nativeName: "English",
  currency: "DKK",
  ui: {
    notice:
      "This page is in English. The rest of votra.dk is in Danish, call us on +45 55 29 28 28 and we will help you in English.",
    indexTitle: "Transport courses in English in Denmark | Votra",
    indexDescription:
      "Haulier (vognmand) courses for truck and van as self-study with the exam at Votra, van driver qualification and taxi driver information, in English.",
    indexH1: "Transport courses in English",
    indexIntro:
      "Votra Danmark ApS is a Danish transport training provider based in Søborg with an exam venue in Ringsted. We are on the Danish Road Traffic Authority's (Færdselsstyrelsen) list of approved exam providers for goods haulier operators, and we provide an interpreter at the exam when you sit it in English, Arabic or Ukrainian.",
    coursesHeading: "Courses you can take in English",
    openCourse: "Read more",
    answerHeading: "Short answer",
    facts: {
      heading: "Key facts",
      course: "Course",
      provider: "Provider",
      delivery: "How it works",
      duration: "Duration",
      priceDa: "Price, exam in Danish",
      priceForeign: "Price, in English (interpreter included)",
      priceAmu: "AMU price (if you qualify)",
      venue: "Where",
      certificate: "What you get",
      entry: "Entry requirements",
      authority: "Authority",
    },
    dates: {
      headingExam: "Upcoming exam dates",
      headingCourse: "Upcoming course dates",
      empty: "No dates are published right now. Call us and we will find a date for you.",
      seats: "seats left",
      full: "Fully booked",
      signUp: "Sign up",
      language: "Language",
      languages: { da: "Danish", en: "English", ar: "Arabic", uk: "Ukrainian", fa: "Persian" },
      allInLanguage: "See all dates with interpretation in English",
      calendarNotice: "You are only seeing dates where this course is offered with interpretation in English.",
      calendarNoticeAll: "You are only seeing dates where interpretation in English is available.",
      calendarBack: "Back to the English pages",
    },
    faqHeading: "Questions and answers",
    sourcesHeading: "Official sources",
    cta: {
      heading: "Ready to start?",
      text: "Call us and we will answer your questions in English, or send us an email and we will get back to you.",
      call: "Call +45 55 29 28 28",
      email: "Email us",
      signUpForm: "Fill in the sign-up form",
      danishPage: "See the full Danish course page",
    },
    danishRequiredBadge: "Danish is a legal requirement for this course",
    fvuCta: "See Votra's Danish preparation (FVU step 2)",
    backHome: "Votra in Danish",
    otherLangs: "Other languages",
    breadcrumb: "English",
  },
  courses: {
    "haulier-truck": {
      slug: "haulier-course-truck",
      metaTitle: "Haulier course for truck in English | Votra",
      metaDescription:
        "Become an approved transport manager for trucks over 3,500 kg. Self-study online, written exam at Votra, interpreter included, three attempts.",
      h1: "Haulier course for truck (godsvognmand), in English",
      short: "Run a goods transport company with trucks over 3,500 kg. Self-study + written exam at Votra.",
      answer:
        "The Danish goods haulier qualification (godsvognmandsuddannelsen) qualifies you to be approved as a transport manager in a company that holds a licence for the carriage of goods for hire or reward with a truck or combination over 3,500 kg. At Votra you study online at your own pace and then sit the written exam with us: Votra Danmark ApS is on Færdselsstyrelsen's list of approved exam providers. The price includes the exam with three attempts, and an interpreter is provided when the exam is taken in English.",
      facts: {
        course: "Goods haulier qualification for truck (godsvognmandsuddannelsen)",
        delivery: "Self-study with online course material + written exam at Votra",
        duration: "Self-study at your own pace. Legal requirement: at least 144 lessons of 60 minutes",
        venue: "Exam at Votra in Ringsted (Sct. Hansgade 9, 2nd floor) and Søborg",
        certificate: "Certificate of professional competence for goods haulage, the basis for being approved as a transport manager",
        entry: "At least 18 years old and able to use a spreadsheet program such as Excel",
        authority: AUTHORITY,
      },
      sections: [
        {
          h2: "How the course works",
          paragraphs: [
            "There is no classroom teaching. You get access to online course material that covers every subject in Annex 1 to the Danish executive order on road haulage (bekendtgørelsen om godskørsel), you read it whenever it suits you, and then you sit the exam on the date you choose.",
            "The legal requirement is that the course covers at least 144 lessons of 60 minutes, including up to 15 minutes of break. That is the level the material covers, but you decide the pace, and there is no attendance requirement.",
          ],
        },
        {
          h2: "The exam",
          paragraphs: [
            "The exam is written. It is prepared and organised by Færdselsstyrelsen and taken in the authority's electronic examination system. Because Votra is an approved exam provider, you sit it at our own premises in Ringsted or Søborg, not at a third party.",
            "The exam consists of 60 multiple choice questions and 6 case assignments, and you need 65 % of the total exam to pass. Three attempts are included in the course price, and we register you for the exam.",
            "When you sit the exam in English, Votra provides an interpreter. That is why the price in a language other than Danish is higher.",
          ],
        },
        {
          h2: "What the exam covers",
          paragraphs: [
            "All 11 subject areas in Annex 1: civil law, commercial law, social and labour law, fiscal law, business and financial management, access to the market, technical standards and operations, road safety, driver-hire activity, negotiation technique and road safety management.",
            "The full list with all sub-points is published on the Danish course page, it is the official syllabus, not a marketing list.",
          ],
        },
        {
          h2: "After you pass",
          paragraphs: [
            "The certificate covers the requirement of professional competence. Before Færdselsstyrelsen issues the licence, the company must also meet the requirements for establishment, financial standing (capital requirement), pay and working conditions, and good repute, and it must appoint an approved transport manager.",
            "The fee is DKK 1,500 for the Community licence and DKK 1,500 per certified copy, the licence is issued for up to 10 years, and the authority aims to decide within 60 days.",
          ],
        },
      ],
      faq: [
        {
          q: "Can I take the exam in English?",
          a: "Yes. Votra provides an interpreter when the exam is taken in English, Arabic or Ukrainian. The interpreter is included in the price for languages other than Danish.",
        },
        {
          q: "Is there any classroom teaching?",
          a: "No. You study the online material at your own pace and then sit the exam at Votra. There is no attendance requirement and no fixed start date.",
        },
        {
          q: "How is the exam structured?",
          a: "60 multiple choice questions and 6 case assignments. You need 65 % of the total exam to pass. It is written and taken in Færdselsstyrelsen's electronic examination system at Votra.",
        },
        {
          q: "How many attempts do I get?",
          a: "Three attempts are included in the course price.",
        },
        {
          q: "Where is the exam held?",
          a: "At Votra in Ringsted (Sct. Hansgade 9, 2nd floor) or in Søborg. Votra Danmark ApS is on Færdselsstyrelsen's list of approved exam providers for goods haulier operators.",
        },
        {
          q: "What are the entry requirements?",
          a: "You must be at least 18 years old and be able to use a spreadsheet program such as Excel, because the financial part is calculated in a spreadsheet.",
        },
        {
          q: "Do I need a truck driving licence?",
          a: "No. The haulier qualification is about running and managing the transport business. A driving licence and driver training are requirements for the person driving the vehicle, not for the transport manager.",
        },
        {
          q: "Is the certificate enough to get the licence?",
          a: "No. It covers professional competence. The company must also meet the requirements for establishment, financial standing, pay and working conditions, and good repute.",
        },
      ],
      sources: [
        FS("Exams for goods haulier operators, Votra is on the provider list", "/privat/chauffoeruddannelser/uddannelser-i-godskoersel/proever-for-godsvognmaend"),
        FS("About road haulage (Færdselsstyrelsen)", "/erhverv/gods/om-godskoersel"),
        { label: "Executive order on road haulage, Annex 1", url: "https://www.retsinformation.dk/eli/lta/2018/1222" },
      ],
    },

    "haulier-van": {
      slug: "haulier-course-van",
      metaTitle: "Haulier course for van in English | Votra",
      metaDescription:
        "Qualify for a goods licence for vans between 2,500 and 3,500 kg. Self-study online, exam at Votra, interpreter included, three attempts.",
      h1: "Haulier course for van (vognmand til varebil), in English",
      short: "Run a goods transport company with vans between 2,500 and 3,500 kg. Self-study + exam at Votra.",
      answer:
        "The van haulier qualification lets you apply for a licence to carry goods for hire or reward with a van or combination between 2,500 kg and 3,500 kg permissible maximum weight. At Votra you study online at your own pace and then sit the written exam with us in Ringsted or Søborg. The price includes the exam with three attempts, and an interpreter is provided when the exam is taken in English.",
      facts: {
        course: "Van haulier qualification (vognmandsuddannelsen til varebil)",
        delivery: "Self-study with online course material + written exam at Votra",
        duration: "Self-study at your own pace. Legal requirement: at least 37 lessons of 60 minutes",
        venue: "Exam at Votra in Ringsted (Sct. Hansgade 9, 2nd floor) and Søborg",
        certificate: "Certificate of professional competence for van haulage, the basis for the van licence",
        entry: "At least 18 years old and able to use a spreadsheet program such as Excel",
        authority: AUTHORITY,
      },
      sections: [
        {
          h2: "How the course works",
          paragraphs: [
            "No classroom teaching and no fixed start date. You get the online material, which covers every subject in Annex 2 to the executive order on road haulage, and you sit the exam on the date you choose.",
            "The legal requirement for the course is at least 37 lessons of 60 minutes, including up to 15 minutes of break.",
          ],
        },
        {
          h2: "The exam",
          paragraphs: [
            "The exam is written, prepared by Færdselsstyrelsen and taken in the authority's electronic examination system. Votra holds the exam at our own premises in Ringsted and Søborg, and we register you for it.",
            "It is a multiple choice exam taken on a computer, and you must answer at least 76 % of the questions correctly to pass. The number of questions is set by Færdselsstyrelsen and is confirmed when you register. Three attempts are included in the price.",
            "When you sit the exam in English, Votra provides an interpreter. That is why the price in a language other than Danish is higher.",
          ],
        },
        {
          h2: "Van or truck, which one do you need?",
          paragraphs: [
            "The van qualification covers goods transport with vans between 2,500 and 3,500 kg and requires at least 37 lessons (Annex 2). If you want to carry goods with a truck or a combination over 3,500 kg, you need the truck qualification, where the requirement is at least 144 lessons (Annex 1). The two licences do not cover each other.",
          ],
        },
      ],
      faq: [
        {
          q: "Can I take the exam in English?",
          a: "Yes. Votra provides an interpreter when the exam is taken in English, Arabic or Ukrainian. It is included in the price for languages other than Danish.",
        },
        {
          q: "How is the exam structured?",
          a: "It is a multiple choice exam taken on a computer, and the pass mark is 76 % correct answers. The number of questions is set by Færdselsstyrelsen and is confirmed when you register. Three attempts are included.",
        },
        {
          q: "Is there classroom teaching?",
          a: "No. You study online at your own pace and then sit the exam at Votra. No attendance requirement, no fixed start date.",
        },
        {
          q: "Which weights does the van licence cover?",
          a: "Vans and combinations with a permissible maximum weight between 2,500 kg and 3,500 kg. Above that you need the truck (goods haulier) qualification.",
        },
        {
          q: "Where is the exam held?",
          a: "At Votra in Ringsted (Sct. Hansgade 9, 2nd floor) and in Søborg.",
        },
        {
          q: "What are the entry requirements?",
          a: "You must be at least 18 years old and be able to use a spreadsheet program such as Excel.",
        },
        {
          q: "Is the certificate enough to get the licence?",
          a: "No. Færdselsstyrelsen also requires establishment, financial standing, pay and working conditions, and good repute before the licence is issued.",
        },
      ],
      sources: [
        FS("Vans, rules for commercial goods transport", "/erhverv/varebil"),
        FS("Van qualifications (Færdselsstyrelsen)", "/privat/chauffoeruddannelser/varebilsuddannelser"),
        { label: "Executive order on road haulage, Annex 2", url: "https://www.retsinformation.dk/eli/lta/2018/1222" },
      ],
    },

    "van-driver-basic": {
      slug: "van-driver-qualification",
      metaTitle: "Van driver qualification (VB GU) in English | Votra",
      metaDescription:
        "The mandatory basic qualification for van drivers carrying goods for hire or reward. Gives you the van driver qualification certificate, valid for five years.",
      h1: "Van driver basic qualification (VB GU), in English",
      short: "The mandatory certificate for driving a van commercially. Three days, exam included.",
      answer:
        "VB GU is the mandatory basic qualification for drivers who carry goods for hire or reward in a van. It gives you the Danish van driver qualification certificate (varebilschaufføruddannelsesbevis), which must be carried while driving and shown on police request. The requirement applies when you carry goods for hire or reward weighing more than 11 kg per item in a van or combination between 2,500 kg and 3,500 kg permissible maximum weight.",
      facts: {
        course: "Van driver basic qualification (VB GU)",
        delivery: "Classroom course run with our partner UCplus, ending with a written exam",
        duration: "3 days",
        venue: "UCplus Skovlunde (Mileparken 12A), our partner's premises",
        certificate: "Van driver qualification certificate (VUB), as a rule valid for five years",
        entry: "None. There are no prerequisites for taking part",
        authority: AUTHORITY,
      },
      sections: [
        {
          h2: "Who needs it?",
          paragraphs: [
            "Drivers carrying goods for hire or reward in a van, where the goods weigh more than 11 kg per item and the van or combination has a permissible maximum weight between 2,500 kg and 3,500 kg.",
            "If you already hold a blue driver qualification card for licence category C (truck), you automatically have the same rights as holders of the van driver qualification certificate.",
          ],
        },
        {
          h2: "The exam",
          paragraphs: [
            "The basic qualification ends with a written exam of 20 multiple choice questions, each with one correct answer. You must answer at least 16 of them correctly, 76 %, to pass, and you have 30 minutes. Candidates with documented dyslexia are given 45 minutes.",
          ],
        },
        {
          h2: "Validity and renewal",
          paragraphs: [
            "The certificate is valid for five years as a rule. To keep it, you must complete a two-day refresher course, where no exam is taken. You may not continue commercial van driving until the refresher course has been completed.",
            "Note: VB stands for varebil, which means van. It has nothing to do with buses.",
          ],
        },
      ],
      faq: [
        { q: "Is VB GU a bus course?", a: "No. VB means varebil, which is Danish for van. VB GU is a van course." },
        { q: "Is there an exam, and how is it structured?", a: "Yes. 20 multiple choice questions in 30 minutes, and you need at least 16 correct answers (76 %) to pass. With documented dyslexia you get 45 minutes." },
        { q: "How long is the certificate valid?", a: "As a rule five years. After that you need the two-day refresher course (VB EU) to keep it." },
        { q: "Are there entry requirements?", a: "No. There are no prerequisites for taking part." },
        { q: "Can I take it in English?", a: "Yes, we run the course in English, Arabic and Ukrainian as well as Danish, but the available languages vary from course date to course date. Call us on +45 55 29 28 28 and we will tell you what is available." },
      ],
      sources: [FS("Van qualifications (Færdselsstyrelsen)", "/privat/chauffoeruddannelser/varebilsuddannelser")],
    },

    "van-driver-refresher": {
      slug: "van-driver-refresher-course",
      metaTitle: "Van driver refresher course (VB EU) in English | Votra",
      metaDescription:
        "The two-day refresher course that renews the Danish van driver qualification certificate. No exam.",
      h1: "Van driver refresher course (VB EU), in English",
      short: "Renew your van driver certificate in two days. No exam.",
      answer:
        "VB EU is the mandatory refresher course that renews the Danish van driver qualification certificate (varebilschaufføruddannelsesbevis). The course lasts two days and there is no exam. If your certificate has expired, you may not continue commercial van driving until the refresher course has been completed.",
      facts: {
        course: "Van driver refresher course (VB EU)",
        delivery: "Classroom course run with our partner UCplus. No exam",
        duration: "2 days",
        venue: "UCplus Skovlunde (Mileparken 12A), our partner's premises",
        certificate: "Renewed van driver qualification certificate (VUB)",
        entry: "A van driver qualification certificate (VUB)",
        authority: AUTHORITY,
      },
      sections: [
        {
          h2: "When do you need it?",
          paragraphs: [
            "The certificate is valid for five years as a rule. Before it expires, you take the two-day refresher course, and the certificate is renewed. There is no exam on the refresher course.",
            "If you are unsure about your deadline, call us and we will help you find the right course date.",
          ],
        },
      ],
      faq: [
        { q: "Is there an exam?", a: "No. There is no exam on the refresher course." },
        { q: "What do I need to take part?", a: "A van driver qualification certificate. If it has expired, you must take the two-day refresher before you resume commercial van driving." },
        { q: "Is VB EU a bus course?", a: "No. VB means varebil, van." },
        { q: "Can I take it in English?", a: "Yes, we run it in English, Arabic and Ukrainian as well as Danish, but the available languages vary from course date to course date. Call us on +45 55 29 28 28." },
      ],
      sources: [FS("Van qualifications (Færdselsstyrelsen)", "/privat/chauffoeruddannelser/varebilsuddannelser")],
    },

    "taxi-driver": {
      slug: "taxi-driver-denmark",
      danishRequired: true,
      metaTitle: "Taxi driver training in Denmark: Danish required | Votra",
      metaDescription:
        "The Danish taxi qualification course is Danish only: the law requires Prøve i Dansk 2 level. Here is what the rules say and how to prepare.",
      h1: "Becoming a taxi driver in Denmark, read this first",
      short: "Important: the taxi course cannot be taken in English. Danish at Prøve i Dansk 2 level is a legal requirement.",
      answer:
        "Important: the Danish taxi qualification course cannot be taken in English, Arabic or Ukrainian. The law requires that you master Danish at a level equivalent to Prøve i Dansk 2 before you can be admitted to an approved course. We are telling you this up front so you do not pay for a course you cannot be admitted to. Everything else, age, driving licence, the course and the two exams, is explained below.",
      facts: {
        course: "Taxi qualification course (taxi-kvalifikationsuddannelsen)",
        delivery: "Classroom teaching, individual driving lessons and two exams. In Danish only",
        duration: "At least 74 hours, including at least 6 individual driving lessons",
        venue: "Votra Ringsted (Sct. Hansgade 9, 2nd floor), on Færdselsstyrelsen's list of approved providers",
        certificate: "The basis for the chauffeur card (chaufførkort), which is issued by Færdselsstyrelsen",
        entry: "Danish at Prøve i Dansk 2 level, 21 years old at the final exams, category B licence for at least 3 years, approved medical certificate and good repute",
        authority: AUTHORITY,
      },
      sections: [
        {
          h2: "The language requirement is a legal requirement",
          paragraphs: [
            "Admission to an approved taxi qualification course requires that you master Danish at a level equivalent to Prøve i Dansk 2, as set out in Annex 4 to the Danish taxi order (taxibekendtgørelsen). In practice it is documented with a certificate for Prøve i Dansk 2 or the Danish exam from the 9th grade of the Danish primary school.",
            "This is not a Votra rule. No approved provider in Denmark may admit you without it, and a decision to refuse admission because of insufficient Danish cannot be appealed to Færdselsstyrelsen.",
          ],
        },
        {
          h2: "The other requirements",
          paragraphs: [
            "You must be at least 21 years old when you sit the final exams and have held a category B driving licence for at least 3 years, periods where the licence was withdrawn do not count. The course must last at least 74 hours, including at least 6 individual driving lessons, and it ends with both a theory exam and a practical driving exam.",
            "The theory exam consists of 30 multiple choice questions in 45 minutes, and you need at least 23 correct answers to pass. The practical exam is driven in a car and assessed by an approved category B driving instructor, with external examination by an instructor from another approved provider.",
            "The chauffeur card itself is issued by Færdselsstyrelsen, which also assesses your health (group 2 requirements: sight, hearing and more) and your good repute. The fee for the card is DKK 1,700, and a medical certificate typically costs DKK 750.",
          ],
        },
        {
          h2: "How to prepare your Danish",
          paragraphs: [
            "Votra also offers preparation for FVU Danish step 2 with an AI tutor that explains in 12 languages, reads the text aloud and gives you realistic practice tests. It is a good way to strengthen your written and spoken Danish.",
            "Please note that FVU Danish step 2 and Prøve i Dansk 2 are two different exams. Call us on +45 55 29 28 28 and we will help you work out which route gets you to the taxi course fastest.",
          ],
        },
        {
          h2: "Courses you can take in English, Arabic or Ukrainian",
          paragraphs: [
            "If your goal is to work in transport now, the haulier qualifications for truck and van are available with an interpreter at the exam. They qualify you to run the transport company, and they do not have the Danish language requirement that the taxi course has.",
          ],
        },
      ],
      faq: [
        {
          q: "Can I take the taxi course in English?",
          a: "No. Admission to an approved taxi qualification course requires Danish at a level equivalent to Prøve i Dansk 2. The requirement is set in the Danish taxi order, not by Votra.",
        },
        {
          q: "How do I document my Danish?",
          a: "As a rule with a certificate for Prøve i Dansk 2 or the Danish exam from the 9th grade of the Danish primary school.",
        },
        {
          q: "What else is required?",
          a: "You must be 21 when you sit the final exams, have held a category B licence for at least 3 years, pass both the theory and the practical exam, and meet Færdselsstyrelsen's health (group 2) and good repute requirements.",
        },
        {
          q: "How is the theory exam structured?",
          a: "30 multiple choice questions in 45 minutes. At least 23 correct answers are needed to pass. The practical exam is assessed as passed or not passed.",
        },
        {
          q: "What does the chauffeur card cost?",
          a: "DKK 1,700 in fee to Færdselsstyrelsen. A medical certificate typically costs DKK 750. The course price is separate.",
        },
        {
          q: "Which Votra courses can I take without Danish?",
          a: "The haulier qualifications for truck and van, and the van driver courses VB GU and VB EU. Votra provides an interpreter at the exam in English, Arabic and Ukrainian.",
        },
      ],
      sources: [
        FS("The taxi qualification course (Færdselsstyrelsen)", "/privat/chauffoeruddannelser/taxi-kvalifikationsuddannelsen"),
        FS("Requirements for the chauffeur card", "/erhverv/taxi/taxichauffoerer/krav-til-chauffoerkort"),
        { label: "The Danish taxi order (BEK no. 1244 of 26/11/2019)", url: "https://www.retsinformation.dk/eli/lta/2019/1244" },
      ],
    },
  },
};

export default en;
